import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SmartImg } from '../../shared/components/Layout';
import { ModuleOffers, Rating, SectionHead } from '../../shared/components/Common';
import Icon from '../../shared/components/Icons';
import { destinations, formatPrice, img } from '../../shared/data/common';
import { usePackages } from '../packages/PackagesContext';

// Aerial tropical coastline, "Flying over a beautiful tropical landscape" (Mixkit, free licence).
const heroVideo = '/videos/hero.mp4';
const heroPoster = img('1507525428034-b723cf961d3e', 1800);

// Hero search "type" choices and the listing filters they map to.
const tripTypes = [
  ['', 'All holidays', {}],
  ['india', 'India packages', { region: 'india' }],
  ['international', 'International packages', { region: 'international' }],
  ['group', 'Group tours', { theme: 'Group Tour' }],
  ['religious', 'Religious tours', { theme: 'Pilgrimage' }],
  ['customized', 'Customized holidays', { theme: 'Customized' }],
];

const typeTiles = [
  { key: 'india', title: 'India Packages', text: 'Beaches, hills & heritage', image: img('1602216056096-3b40cc0c9944', 700) },
  { key: 'international', title: 'International Packages', text: 'Visa-assisted getaways', image: img('1537996194471-e657df975ab4', 700) },
  { key: 'group', title: 'Group Tours', text: 'Fixed departures with a tour manager', image: img('1508009603885-50cf7c579365', 700) },
  { key: 'religious', title: 'Religious Tours', text: 'Yatras with darshan assistance', image: img('1561361513-2d000a50f0dc', 700) },
];

const stats = [['2,500+', 'Holiday itineraries'], ['50,000+', 'Happy families'], ['120+', 'Group departures a year'], ['4.7★', 'Average rating']];

const whyUs = [
  { icon: 'compass', title: 'Handcrafted itineraries', text: 'Planned by destination experts who have travelled every route.' },
  { icon: 'wallet', title: 'All-inclusive pricing', text: 'Stays, transfers & sightseeing in one price — no surprises.' },
  { icon: 'headset', title: '24x7 trip support', text: 'A holiday expert on call from booking until you are home.' },
  { icon: 'lock', title: 'Easy, secure payments', text: 'UPI, cards, net banking and no-cost EMI options.' },
];

function PackageCard({ p }) {
  return (
    <Link to={`/packages/${p.id}`} className="card hover">
      <div className="card-img">
        <SmartImg src={p.image} alt={p.title} />
        <span className="badge badge-dark">{p.nights}N / {p.nights + 1}D</span>
      </div>
      <div className="card-body">
        <span className="muted small"><Icon name="pin" size={12} /> {p.cities.join(' · ')}</span>
        <h3>{p.title}</h3>
        <div className="includes">
          {p.includes.slice(0, 3).map((x) => <span key={x}><Icon name="check" size={12} strokeWidth={2.5} /> {x}</span>)}
        </div>
        <div className="price-row">
          <div>
            {p.oldPrice && <s className="muted small">{formatPrice(p.oldPrice)}</s>}
            <div>{p.enquiryOnly && <span className="muted small">From </span>}<b className="price">{formatPrice(p.price)}</b><span className="muted small"> /person</span></div>
          </div>
          <Rating value={p.rating} compact />
        </div>
      </div>
    </Link>
  );
}

function VideoHero({ packages, menus }) {
  const navigate = useNavigate();
  const video = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [destination, setDestination] = useState('');
  const [type, setType] = useState('');
  const places = [...new Set(packages.map((p) => p.destination))].sort();

  // Respect "reduce motion": keep the poster still unless the visitor presses play.
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      video.current?.pause();
      setPlaying(false);
    }
  }, []);

  const toggle = () => {
    if (playing) video.current.pause();
    else video.current.play();
    setPlaying(!playing);
  };

  const search = (event) => {
    event.preventDefault();
    const filters = { ...tripTypes.find(([k]) => k === type)[2], ...(destination ? { destination } : {}) };
    navigate(`/packages/results?${new URLSearchParams(filters)}`);
  };

  return (
    <section className="video-hero">
      <video ref={video} className="video-hero-media" autoPlay muted loop playsInline preload="auto" poster={heroPoster} aria-hidden="true">
        <source src={heroVideo} type="video/mp4" />
      </video>
      <div className="video-hero-shade" />
      <div className="container video-hero-content">
        <span className="video-hero-eyebrow"><Icon name="packages" size={14} /> Holidays · Group tours · Yatras</span>
        <h1>Holidays crafted for every kind of traveller</h1>
        <p>Handpicked India &amp; international packages with stays, transfers and sightseeing — or tell us your dream trip and we’ll build it for you.</p>

        <form className="hero-pkg-search" onSubmit={search} role="search">
          <label>
            <span>Where to?</span>
            <select value={destination} onChange={(e) => setDestination(e.target.value)} aria-label="Destination">
              <option value="">Any destination</option>
              {places.map((d) => <option key={d}>{d}</option>)}
            </select>
          </label>
          <label>
            <span>Holiday type</span>
            <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Holiday type">
              {tripTypes.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </label>
          <button className="btn btn-coral lg"><Icon name="search" size={18} /> Find holidays</button>
        </form>

        <div className="hero-chips">
          {menus.map((m) => <Link key={m.key} to={m.to}><Icon name={m.icon} size={14} /> {m.label}</Link>)}
        </div>
      </div>
      <button type="button" className="video-toggle" onClick={toggle} aria-label={playing ? 'Pause background video' : 'Play background video'}>
        {playing ? <span className="pause-glyph" aria-hidden="true" /> : <span className="play-glyph" aria-hidden="true" />}
      </button>
    </section>
  );
}

export default function Home() {
  const { packages, menus } = usePackages();
  const bestSellers = [...packages].filter((p) => p.reviews).sort((a, b) => b.reviews - a.reviews).slice(0, 4);
  const groupTours = packages.filter((p) => p.theme === 'Group Tour').slice(0, 4);
  const religious = packages.filter((p) => p.theme === 'Pilgrimage').slice(0, 4);
  const menuOf = (key) => menus.find((m) => m.key === key);
  const hasCustomized = packages.some((p) => p.enquiryOnly);

  return (
    <>
      <VideoHero packages={packages} menus={menus} />

      <section className="container trust-bar">
        {stats.map(([n, l]) => <div key={l}><b>{n}</b><span>{l}</span></div>)}
      </section>

      <section className="section container">
        <SectionHead title="Find your kind of holiday" sub="From family beach breaks to guided yatras" />
        <div className="type-grid">
          {typeTiles.map((t) => {
            const menu = menuOf(t.key);
            return (
              <Link key={t.key} to={menu.to} className="type-tile">
                <SmartImg src={t.image} alt={t.title} />
                <div className="type-tile-info">
                  <h3>{t.title}</h3>
                  <span>{t.text}</span>
                  <small>{menu.items.length} {t.key === 'group' || t.key === 'religious' ? 'tours' : 'destinations'} <Icon name="arrow" size={12} /></small>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="section band">
        <div className="container">
          <SectionHead title="Best-selling holiday packages" sub="Flights, stays, transfers & sightseeing — all included" link={{ to: '/packages/results', label: 'View all packages' }} />
          <div className="card-grid">{bestSellers.map((p) => <PackageCard key={p.id} p={p} />)}</div>
        </div>
      </section>

      <section className="section container">
        <SectionHead title="Trending destinations" sub="Most booked by Indian travellers this season" link={{ to: '/packages/results', label: 'Explore holidays' }} />
        <div className="dest-grid">
          {destinations.map((d) => (
            <Link key={d.name} to={`/packages/results?destination=${d.name}`} className="dest-card">
              <SmartImg src={d.image} alt={d.name} />
              <div className="dest-info">
                <span className="dest-country">{d.country}</span>
                <h3>{d.name}</h3>
                <span className="dest-price">Holidays from <b>{formatPrice(d.from)}</b></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {groupTours.length > 0 && (
        <section className="section container">
          <SectionHead title="Upcoming group tours" sub="Travel with like-minded people and a dedicated tour manager" link={{ to: menuOf('group').to, label: 'All group tours' }} />
          <div className="card-grid">{groupTours.map((p) => <PackageCard key={p.id} p={p} />)}</div>
        </section>
      )}

      <section className="section container">
        <SectionHead title="Holiday offers" />
        <ModuleOffers module="packages" />
      </section>

      {religious.length > 0 && (
        <section className="section band">
          <div className="container">
            <SectionHead title="Religious tours & yatras" sub="Comfortable stays, private cabs and darshan assistance" link={{ to: menuOf('religious').to, label: 'All religious tours' }} />
            <div className="card-grid">{religious.map((p) => <PackageCard key={p.id} p={p} />)}</div>
          </div>
        </section>
      )}

      <section className="section container">
        <div className="custom-cta">
          <div>
            <h2>Dreaming of something different?</h2>
            <p>Tell us where, when and your budget — our holiday experts will design a trip just for you.</p>
          </div>
          <Link to={hasCustomized ? '/packages/results?theme=Customized' : '/packages/results'} className="btn btn-coral lg">Plan a customized holiday</Link>
        </div>
      </section>

      <section className="section container">
        <h2 className="center-title">Why book your holiday with Trawtel?</h2>
        <div className="why-grid">
          {whyUs.map((w) => (
            <div key={w.title} className="why-card">
              <span className="why-icon"><Icon name={w.icon} size={24} /></span>
              <div><h3>{w.title}</h3><p className="muted">{w.text}</p></div>
            </div>
          ))}
        </div>
      </section>

      <section className="section container">
        <div className="newsletter">
          <div>
            <h2>Never miss a holiday deal</h2>
            <p>Get early-bird group departures, festive offers and new itineraries straight to your inbox.</p>
          </div>
          <form className="newsletter-form" onSubmit={(e) => { e.preventDefault(); e.target.reset(); alert('Thanks for subscribing!'); }}>
            <input type="email" required placeholder="Enter your email address" aria-label="Email address" />
            <button className="btn btn-coral">Subscribe</button>
          </form>
        </div>
      </section>
    </>
  );
}
